import { useLocation } from 'wouter';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@/ui";
import { ArrowLeft, MapPin, Clock, Users, Briefcase, CheckCircle, Star } from 'lucide-react';

export default function JobPosting() {
  const [, setLocation] = useLocation();
  
  return (
    <div>
      <main className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => setLocation('/careers')}
          className="mb-6 flex items-center text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Careers
        </Button>

        {/* Job Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="text-sm">Product</Badge>
            <Badge variant="outline" className="text-sm">Full-time</Badge>
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Clinical Product Manager (Agentic AI Healthcare Platform)
          </h1>
          <div className="flex items-center gap-6 text-muted-foreground">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              <span>Remote (U.S. or Global)</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              <span>Full-time</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* About Aiscendus */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  About Aiscendus
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  Aiscendus is transforming healthcare through an Agentic AI platform that unifies clinical intelligence, 
                  automation, and interoperability. Our intelligent agents streamline chart preparation, ambient listening, 
                  care coordination, and population health management—deeply integrated with leading EHRs such as Epic, 
                  Cerner, and AthenaHealth. We build solutions that empower clinicians, reduce administrative burden, 
                  and enable smarter, data-driven care delivery.
                </p>
              </CardContent>
            </Card>

            {/* Role Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Role Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  We are seeking a Clinical Product Manager to bridge clinical expertise with AI-driven product innovation. 
                  You will define, prioritize, and guide the development of next-generation Agentic AI solutions that address 
                  real-world healthcare workflow challenges. This role is ideal for a clinically experienced professional 
                  passionate about reimagining how technology supports care delivery through intelligent automation.
                </p>
              </CardContent>
            </Card>

            {/* Key Responsibilities */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Key Responsibilities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Translate clinical workflows and operational pain points into actionable product requirements, 
                      user stories, and design specifications.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Collaborate with AI engineers, data scientists, and UX teams to build clinically meaningful, 
                      high-impact AI products.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Define and refine use cases for Agentic AI modules (e.g., ChartPrep, Ambient Scribe, 
                      Care Coordination, CDS, Claims, and Revenue Cycle).
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Ensure alignment between clinical usability, AI capabilities, and compliance requirements 
                      (HIPAA, SOC 2, HITRUST).
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Evaluate and validate AI and decision support tools for accuracy, relevance, and safety 
                      in clinical use.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Engage clinicians and customers to gather feedback and continuously enhance workflow fit and usability.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Collaborate on EHR and EMR integration strategies using FHIR, HL7, and interoperability standards.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Support roadmap planning, product documentation, and cross-functional delivery alignment.
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Preferred Qualifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Preferred Qualifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      5+ years of experience in clinical informatics, digital health, or healthcare product management.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Background in a clinical discipline (MD, RN, NP, PA, PharmD, or equivalent experience) strongly preferred.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Strong understanding of care delivery workflows across ambulatory, acute, and value-based care settings.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Proven experience defining or managing AI/ML or digital health solutions.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Experience working with leading EHR/EMR systems (Epic, Cerner, AthenaHealth, Allscripts, or MEDITECH).
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Familiarity with medical coding standards (CPT, ICD-10, SNOMED, LOINC, HCPCS) is a plus.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Ability to translate between clinical and technical domains (AI, APIs, data models, orchestration).
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      Excellent communication and cross-functional collaboration skills.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">
                      <strong>Bonus:</strong> exposure to Rust-based systems, GraphQL APIs, or Agentic AI architectures 
                      (plan–react–reflect workflows).
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Apply Now Card */}
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Ready to Apply?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Join our team and help transform healthcare through intelligent AI solutions.
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => setLocation('/careers/apply')}
                >
                  Apply Now
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setLocation('/careers')}
                >
                  View All Positions
                </Button>
              </CardContent>
            </Card>

            {/* Job Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Job Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Location</h4>
                  <p className="text-sm">Remote (U.S. or Global)</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Type</h4>
                  <p className="text-sm">Full-time</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Department</h4>
                  <p className="text-sm">Product Management</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Experience Level</h4>
                  <p className="text-sm">Senior (5+ years)</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
